import { Navigate, Outlet, useLocation, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useDiet } from "@/contexts/DietContext";
import { Home, UtensilsCrossed, ClipboardList, CreditCard, User, LogOut, Menu, BookOpen, BarChart3, Lock, Calculator, ChefHat, FileText, AlertTriangle, Users, LogIn } from "lucide-react";

const PUBLIC_ROUTES = ["/dashboard", "/diet-builder", "/plans"];
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const PLAN_DISPLAY_NAMES: Record<string, string> = {
  plus: "PRO FIT PLUS",
  complete: "PRO FIT MAX",
  basic: "PRO FIT",
};

const AppLayout = () => {
  const { user, logout, loading } = useAuth();
  const { selectedPlan, subscriptionExpiry, planLoading } = useDiet();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loadingStuck, setLoadingStuck] = useState(false);
  const [showExpiredModal, setShowExpiredModal] = useState(false);

  useEffect(() => {
    if (!loading) { setLoadingStuck(false); return; }
    const t = setTimeout(() => setLoadingStuck(true), 10000);
    return () => clearTimeout(t);
  }, [loading]);

  // Detect expired plan
  useEffect(() => {
    if (planLoading || !user) return;
    
    const dismissed = sessionStorage.getItem("expired_plan_dismissed");
    if (dismissed) return;

    // If effective plan is free but there's a past expires_at, the plan expired
    if (selectedPlan === "free" && subscriptionExpiry) {
      const expiryDate = new Date(subscriptionExpiry);
      if (expiryDate < new Date()) {
        setShowExpiredModal(true);
      }
    }
  }, [selectedPlan, subscriptionExpiry, planLoading, user]);

  const handleDismissExpired = () => {
    sessionStorage.setItem("expired_plan_dismissed", "true");
    setShowExpiredModal(false);
  };

  const handleGoToPlans = () => {
    sessionStorage.setItem("expired_plan_dismissed", "true");
    setShowExpiredModal(false);
    navigate("/plans");
  };

  if (loading) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-4">
      <span className="text-muted-foreground">Carregando...</span>
      {loadingStuck && (
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity"
        >
          Recarregar página
        </button>
      )}
    </div>
  );

  const isPublicRoute = PUBLIC_ROUTES.includes(location.pathname);
  if (!user && !isPublicRoute) return <Navigate to="/login" replace />;

  const hasPlan = (min: "basic" | "plus" | "complete") => {
    const tiers = ["basic", "plus", "complete"];
    const userTier = tiers.indexOf(selectedPlan || "");
    const minTier = tiers.indexOf(min);
    return userTier >= minTier;
  };

  const requiresAuth = (to: string) => !user && !PUBLIC_ROUTES.includes(to);

  const navItems = [
    { to: "/dashboard", icon: Home, label: "Início", locked: false },
    { to: "/diet-builder", icon: UtensilsCrossed, label: "Montar Dieta", locked: false },
    { to: "/my-diet", icon: ClipboardList, label: "Minha Dieta", locked: requiresAuth("/my-diet") },
    { to: "/recipe-calculator", icon: Calculator, label: "Calculadora de Calorias", locked: requiresAuth("/recipe-calculator") || !hasPlan("plus") },
    { to: "/diary", icon: BookOpen, label: "Diário", locked: requiresAuth("/diary") || !hasPlan("plus") },
    { to: "/metrics", icon: BarChart3, label: "Medidas Corporais", locked: requiresAuth("/metrics") || !hasPlan("plus") },
    { to: "/recipes", icon: ChefHat, label: "Receitas", locked: requiresAuth("/recipes") || !hasPlan("complete") },
    { to: "/materiais", icon: FileText, label: "Materiais", locked: requiresAuth("/materiais") || !hasPlan("complete") },
    { to: "/comunidade", icon: Users, label: "Comunidade", locked: requiresAuth("/comunidade") || !hasPlan("complete") },
    { to: "/plans", icon: CreditCard, label: "Planos", locked: false },
    { to: "/profile", icon: User, label: "Perfil", locked: requiresAuth("/profile") },
  ];

  // Determine expired plan name from expires_at context
  const expiredPlanName = (() => {
    // We don't have raw_plan on the frontend easily, but we can show a generic message
    return "seu plano";
  })();

  return (
    <div className="min-h-screen bg-background flex">
      {/* Expired plan modal */}
      <Dialog open={showExpiredModal} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-destructive" />
              </div>
              <DialogTitle className="text-xl">Plano Expirado</DialogTitle>
            </div>
            <DialogDescription className="text-base leading-relaxed">
              Seu plano premium expirou e você perdeu acesso aos módulos exclusivos.
              Para continuar utilizando todas as funcionalidades, renove seu plano.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex flex-col sm:flex-row gap-2 mt-4">
            <Button variant="outline" onClick={handleDismissExpired} className="w-full sm:w-auto">
              Entendi
            </Button>
            <Button onClick={handleGoToPlans} className="w-full sm:w-auto">
              Renovar Plano
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 bg-foreground/20 md:hidden" onClick={() => setMobileOpen(false)} />
      )}

      <aside className={`fixed md:sticky top-0 left-0 z-50 h-screen w-[220px] bg-card border-r border-border flex flex-col transition-transform duration-200 ${mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}`}>
        <div className="flex items-center gap-2.5 px-5 h-16 border-b border-border">
          <img src="/logo_final.png" alt="Logo Nutrir Corpo & Mente" className="w-8 h-8 rounded-lg object-cover" />
          <span className="font-bold text-foreground text-sm leading-tight">Nutrir Corpo<br/>& Mente</span>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1">
          {navItems.map(item => {
            const active = location.pathname === item.to;
            const needsLogin = !user && item.to !== "/plans" && !PUBLIC_ROUTES.includes(item.to);
            const target = needsLogin ? `/login?next=${encodeURIComponent(item.to)}` : item.to;
            return (
              <Link
                key={item.to}
                to={target}
                onClick={() => setMobileOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  active
                    ? "bg-primary text-primary-foreground"
                    : item.locked
                    ? "text-muted-foreground/40 hover:text-muted-foreground/60"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted"
                }`}
              >
                <item.icon className="w-[18px] h-[18px]" />
                {item.label}
                {item.locked && <Lock className="w-3 h-3 ml-auto" />}
              </Link>
            );
          })}
        </nav>

        <div className="border-t border-border px-4 py-4 space-y-3">
          {user ? (
            <>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center">
                  <User className="w-4 h-4 text-muted-foreground" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{user.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                </div>
              </div>
              <button
                onClick={logout}
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-destructive transition-colors w-full"
              >
                <LogOut className="w-4 h-4" />
                Sair
              </button>
            </>
          ) : (
            <Link
              to="/login"
              onClick={() => setMobileOpen(false)}
              className="flex items-center justify-center gap-2 w-full py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity"
            >
              <LogIn className="w-4 h-4" />
              Entrar / Criar conta
            </Link>
          )}
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="md:hidden sticky top-0 z-30 bg-card/90 backdrop-blur-lg border-b border-border h-14 flex items-center px-4">
          <button onClick={() => setMobileOpen(true)} className="text-foreground">
            <Menu className="w-6 h-6" />
          </button>
          <span className="ml-3 font-bold text-foreground">Nutrir Corpo & Mente</span>
        </header>

        <main className="flex-1 px-4 md:px-8 lg:px-10 py-6 md:py-8 max-w-4xl w-full mx-auto overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AppLayout;
